/**
 * One student in one class session. The columns split by share state
 * (`splitReviewCard`): the left is still to decide. The right is decided.
 * Shared items stay visible. Don't-share and hidden scans stay off the
 * card until its show-hidden box is on. Sharing slides an item out of the
 * left and into the right; unsharing slides it back. An empty left
 * collapses the card, turns it green, and can be opened again.
 * A photo sits above the note of the person who took it, or above the
 * notes when that person did not write one.
 */
import { Checkbox, Spin } from "antd"
import dayjs from "dayjs"
import { AnimatePresence, type HTMLMotionProps } from "motion/react"
import { useEffect, useRef, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import { ArtworkLightbox } from "#components/ArtworkTiles/artworkTilePrimitives"
import ClickableStudentName from "#components/ClickableStudentName"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  type FamilyReactionEntry,
  type FamilyReactionType,
  type StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { useStandoutMediaUrl } from "#hooks/newHooks/useStandoutMediaUrl"
import {
  STANDARD_DATE_TIME_FORMAT,
  STUDENT_DOB_FORMAT,
} from "#utils/dateHelpers"

import { ClassImageReviewRow } from "./ClassImageReviewList"
import ClearedCelebration, { useJustCleared } from "./ClearedCelebration"
import { useFamilyDelivery } from "./familyDeliveryContext"
import {
  type EarlierComment,
  type HistoryCard,
  isHistoryMessageShared,
  type SessionAudio,
  type SessionComment,
  type SessionPhoto,
  skillAbbreviation,
  splitReviewCard,
} from "./reviewCardHistory"
import { REVIEW_MUTED, reviewEdgeColor } from "./reviewCardLayout"
import {
  ItemAttribution,
  itemBylineRowStyle,
  ItemKindTitle,
} from "./reviewItemBody"
import SendCountdownLabel from "./SendCountdownLabel"
import StandoutMomentCard from "./StandoutMomentCard"
import { classScanAuthor } from "./standoutReviewCardHelpers"
import { type StandoutReviewJoins } from "./standoutReviewHelpers"
import { levelForStudentSession } from "./standoutSessionGroups"
import { StoryTimeShareSwitch } from "./StoryTimeReviewList"
import {
  familyRepeatMomentView,
  type FamilyRepeatStudentView,
} from "./useFamilyRepeatJudgment"

const REACTION_LABEL: Record<FamilyReactionType, string> = {
  paw: "Paw",
  star: "Star",
  smiley: "Smiley",
  palette: "Palette",
  thumbsUp: "Thumbs up",
}

const SessionAudioCard = ({
  clip,
  studentId,
  classSessionId,
  joins,
}: {
  clip: SessionAudio
  studentId: string
  classSessionId: string | null
  joins: StandoutReviewJoins
}) => {
  const theme = useThemeContext()
  const edgeColor = reviewEdgeColor(clip.shared ? "shared" : "unshared", {
    green: theme.eric.green,
    red: theme.eric.red,
    gray: REVIEW_MUTED,
  })
  const author = clip.createdBy
    ? (joins.usersById[clip.createdBy]?.name?.trim() ?? "")
    : ""
  const when = [
    clip.at > 0 ? dayjs(clip.at).format(STANDARD_DATE_TIME_FORMAT) : "",
    clip.durationSeconds != null ? `${clip.durationSeconds}s` : "",
  ]
    .filter((part) => part.length > 0)
    .join(" · ")
  return (
    <div
      data-testid="sr-session-audio"
      data-audio-id={clip.id}
      data-shared={clip.shared ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${edgeColor}`,
        borderLeft: `4px solid ${edgeColor}`,
        backgroundColor: theme.white,
        minWidth: 0,
      }}
    >
      <div style={itemBylineRowStyle}>
        <ItemKindTitle>Storytime</ItemKindTitle>
        <StoryTimeShareSwitch
          storyTimeId={clip.id}
          studentId={studentId}
          classSessionId={classSessionId}
          shared={clip.shared}
        />
      </div>
      <audio controls preload="none" src={clip.url} style={{ width: "100%" }} />
      <ItemAttribution verb="written by" name={author} when={when} />
    </div>
  )
}

const RepeatJudgment = ({
  repeat,
  card,
}: {
  repeat?: FamilyRepeatStudentView
  card: HistoryCard
}) => {
  const theme = useThemeContext()
  if (!repeat) return null
  const onCard = new Set(
    card.items.flatMap((item) => (item.kind === "message" ? [item.id] : []))
  )
  const shownIds = repeat.momentIds.filter((id) => onCard.has(id))
  if (shownIds.length === 0) return null
  const lines = shownIds.map((id) => ({
    momentId: id,
    ...familyRepeatMomentView(repeat, id),
  }))
  const repeats = lines.some((line) => line.similar)
  return (
    <div
      data-testid="sr-repeat-judgment"
      data-phase={repeat.phase}
      data-repeats={repeats ? "true" : "false"}
      style={{
        marginTop: 4,
        fontSize: 12,
        lineHeight: "16px",
        color: repeats ? theme.eric.red : REVIEW_MUTED,
        fontWeight: repeats ? 600 : 400,
      }}
    >
      {repeat.phase === "checking" ? (
        <span
          data-testid="sr-repeat-checking"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Spin size="small" />
          Checking for similar notes
        </span>
      ) : null}
      {repeat.phase === "failed" ? "Could not check for similar notes" : null}
      {repeat.phase === "ready"
        ? lines.flatMap((line) =>
            line.reason ? (
              <div key={line.momentId} data-moment-id={line.momentId}>
                {line.similar ? "Similar" : "New"} — {line.reason}
              </div>
            ) : (
              []
            )
          )
        : null}
    </div>
  )
}

const headerLine = {
  minWidth: 0,
  overflow: "hidden" as const,
  whiteSpace: "nowrap" as const,
  textOverflow: "ellipsis" as const,
}

const reactionEntries = (
  moment: StandoutMomentConstructor
): FamilyReactionEntry[] =>
  Object.values(moment.familyReactions ?? {}).filter(
    (entry): entry is FamilyReactionEntry =>
      !!entry && Array.isArray(entry.types) && entry.types.length > 0
  )

const FamilyReactions = ({ moment }: { moment: StandoutMomentConstructor }) => {
  const entries = reactionEntries(moment)
  if (entries.length === 0) return null
  return (
    <ul
      data-testid="sr-family-reactions"
      data-moment-id={moment.id}
      style={{
        margin: "6px 0 0",
        padding: 0,
        listStyle: "none",
        fontSize: 12,
        lineHeight: "16px",
        color: REVIEW_MUTED,
      }}
    >
      {entries.map((entry) => (
        <li key={`${entry.mobileUid}|${entry.elementId}`}>
          {entry.studentFirstName || "Family"}
          {" · "}
          {entry.actorRole === "student" ? "Student" : "Guardian"}
          {" · "}
          {entry.types.map((type) => REACTION_LABEL[type]).join(", ")}
        </li>
      ))}
    </ul>
  )
}

const MomentPhotoThumb = ({
  photo,
  thumb,
  wide,
}: {
  photo: SessionPhoto
  thumb: boolean
  wide: boolean
}) => {
  const theme = useThemeContext()
  const [open, setOpen] = useState(false)
  const hooked = useStandoutMediaUrl(photo.storagePath)
  const src = photo.url || hooked || ""
  const size = thumb ? 100 : 160
  return (
    <div
      data-testid={wide || thumb ? "sr-shared-photo" : "sr-placed-photo"}
      data-photo-id={photo.id}
      data-thumb={thumb ? "100" : undefined}
      data-wide={wide ? "true" : undefined}
      style={wide ? { width: "100%", minWidth: 0 } : undefined}
    >
      <button
        type="button"
        aria-label={photo.caption || "Open photo"}
        onClick={() => {
          if (src) setOpen(true)
        }}
        style={{
          padding: 0,
          border: 0,
          background: "transparent",
          cursor: src ? "pointer" : "default",
          width: wide ? "100%" : undefined,
        }}
      >
        {src ? (
          <img
            src={src}
            alt={photo.caption || "Class photo"}
            style={
              wide
                ? {
                    width: "100%",
                    height: "auto",
                    maxHeight: 360,
                    objectFit: "contain",
                    display: "block",
                    backgroundColor: theme.gray[3],
                  }
                : {
                    width: size,
                    height: size,
                    objectFit: "cover",
                    display: "block",
                    backgroundColor: theme.gray[3],
                  }
            }
          />
        ) : (
          <span style={{ color: REVIEW_MUTED, fontSize: 12 }}>Photo</span>
        )}
      </button>
      {photo.caption ? (
        <div
          data-testid="sr-photo-caption"
          style={{ fontSize: 12, marginTop: 4 }}
        >
          {photo.caption}
        </div>
      ) : null}
      <ArtworkLightbox
        open={open}
        artworkId={photo.id}
        src={src}
        caption={photo.caption || "Class photo"}
        rotation={0}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}

const SessionPhotoView = ({
  photo,
  joins,
  thumb,
  wide = false,
}: {
  photo: SessionPhoto
  joins: StandoutReviewJoins
  thumb: boolean
  /** The picture fills its column, the same width as the comment cards. */
  wide?: boolean
}) => {
  if (!photo.scan) {
    return <MomentPhotoThumb photo={photo} thumb={thumb} wide={wide} />
  }
  const caption = photo.caption
  return (
    <div
      data-testid={wide || thumb ? "sr-shared-photo" : "sr-placed-photo"}
      data-photo-id={photo.id}
      data-thumb={thumb ? "100" : undefined}
      data-wide={wide ? "true" : undefined}
      style={wide ? { width: "100%", minWidth: 0 } : undefined}
    >
      <ClassImageReviewRow
        row={photo.scan}
        imageSize={wide || !thumb ? undefined : 100}
        asItem={{
          bylineName:
            classScanAuthor(
              photo.scan.artwork,
              joins.usersById,
              joins.studentsById
            )?.name ?? "",
          bylineWhen: photo.scan.artwork.timeCreated
            ? dayjs(photo.scan.artwork.timeCreated).format(
                STANDARD_DATE_TIME_FORMAT
              )
            : "",
        }}
      />
      {caption ? (
        <div
          data-testid="sr-photo-caption"
          style={{ fontSize: 12, marginTop: 4 }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  )
}

const authorRole = (
  moment: StandoutMomentConstructor,
  joins: StandoutReviewJoins
): "teacher" | "ta" => {
  if (moment.assistantId?.trim()) return "ta"
  const role = moment.createdBy
    ? joins.usersById[moment.createdBy]?.role
    : undefined
  return role === "ASSISTANT" ? "ta" : "teacher"
}

const SessionReviewCard = ({
  card,
  joins,
  repeat,
}: {
  card: HistoryCard
  joins: StandoutReviewJoins
  repeat?: FamilyRepeatStudentView
}) => {
  const theme = useThemeContext()
  const student = joins.studentsById[card.studentId]
  const session = (() => {
    if (!card.classSessionId) return null
    for (const list of Object.values(joins.sessionsByClassId)) {
      const found = list.find((row) => row.id === card.classSessionId)
      if (found) return found
    }
    return null
  })()
  const projectId = session?.projectId || card.projectId
  const lookedUpProject = projectId
    ? (joins.projectsById[projectId]?.name?.trim() ?? "")
    : ""
  // Sessions and project docs arrive in separate reads, and the project
  // subscription key changes as those ids settle. Hold the last name so the
  // line does not unmount during that gap.
  const heldProjectName = useRef("")
  if (lookedUpProject) heldProjectName.current = lookedUpProject
  const projectName = lookedUpProject || heldProjectName.current
  const scans = [
    ...card.topPhotos.flatMap((photo) => (photo.scan ? [photo.scan] : [])),
    ...card.comments.flatMap((comment) =>
      comment.photos.flatMap((photo) => (photo.scan ? [photo.scan] : []))
    ),
  ]
  const level = levelForStudentSession(
    {
      studentId: card.studentId,
      moments: card.comments.map((comment) => comment.moment),
      storyTimes: [],
      classImages: scans,
    },
    joins
  )
  const skill = skillAbbreviation(level)
  const born = student?.dob ? dayjs(student.dob) : null
  const dobLabel = born?.isValid() ? born.format(STUDENT_DOB_FORMAT) : ""
  const [avatarFailed, setAvatarFailed] = useState(false)
  const [opened, setOpened] = useState(false)
  const [showHidden, setShowHidden] = useState(false)
  const delivery = useFamilyDelivery()
  const notifyAt = delivery.notifyAt(card.studentId, card.classSessionId)
  const anyBlocked = card.items.some((item) =>
    item.kind === "message"
      ? item.moment.dontShare === true
      : item.scan.artwork.dontShare === true
  )
  const anyUnshared = card.items.some((item) => {
    if (item.kind === "message") return !isHistoryMessageShared(item.moment)
    return item.scan.artwork.isShared !== true
  })
  const summary = anyBlocked ? "blocked" : anyUnshared ? "unshared" : "shared"
  const avatarUrl = student?.avatar?.trim() ?? ""
  const showAvatarImage =
    avatarUrl.length > 0 && !avatarUrl.includes("placehold.co") && !avatarFailed
  const nameLabel = student?.name?.trim() || card.studentId
  const initials = nameLabel
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .map((part) => part[0] ?? "")
  const avatarLetters = (
    initials.length > 1
      ? `${initials[0]}${initials[initials.length - 1]}`
      : (initials[0] ?? "")
  ).toUpperCase()
  const classNameFor = (classId: string | undefined): string =>
    (classId ? joins.classesById[classId]?.name?.trim() : "") || "Class"

  const columns = splitReviewCard(card)
  const { open, blocked, hidden, shared } = columns
  const justCleared = useJustCleared(columns.cleared)
  const wasCleared = useRef(columns.cleared)
  useEffect(() => {
    if (columns.cleared && !wasCleared.current) setOpened(false)
    wasCleared.current = columns.cleared
  }, [columns.cleared])
  const collapsed = columns.cleared && !opened
  const hiddenCount =
    blocked.notes.length +
    blocked.photos.length +
    hidden.notes.length +
    hidden.photos.length
  const firstName =
    student?.firstName?.trim() || nameLabel.split(/\s+/)[0] || "this kid"

  const renderPhotos = (
    photos: readonly SessionPhoto[],
    thumb: boolean,
    wide: boolean,
    animation: HTMLMotionProps<"div">
  ) => (
    <AnimatePresence initial={false} mode="popLayout">
      {photos.map((photo) => (
        <AnimateInFlex
          key={photo.id}
          animation={animation}
          style={wide ? { width: "100%" } : undefined}
        >
          <SessionPhotoView
            photo={photo}
            joins={joins}
            thumb={thumb}
            wide={wide}
          />
        </AnimateInFlex>
      ))}
    </AnimatePresence>
  )

  const photoDivider = (
    photos: readonly SessionPhoto[],
    ruled: boolean,
    thumb: boolean,
    wide: boolean,
    animation: HTMLMotionProps<"div">
  ) =>
    photos.length > 0 ? (
      <div
        data-testid="sr-photo-divider"
        data-wide={wide ? "true" : undefined}
        style={{
          display: "flex",
          flexDirection: wide ? "column" : "row",
          flexWrap: wide ? "nowrap" : "wrap",
          alignItems: wide ? "stretch" : undefined,
          gap: 8,
          width: wide ? "100%" : undefined,
          paddingBottom: ruled ? 10 : 0,
          borderBottom: ruled ? `1px solid ${theme.border}` : undefined,
        }}
      >
        {renderPhotos(photos, thumb, wide, animation)}
      </div>
    ) : null

  const renderAudio = (
    clips: readonly SessionAudio[],
    animation: HTMLMotionProps<"div">,
    style?: { marginBottom?: number }
  ) => (
    <AnimatePresence initial={false} mode="popLayout">
      {clips.map((clip) => (
        <AnimateInFlex
          key={clip.id}
          animation={animation}
          col
          style={{ minWidth: 0, ...style }}
        >
          <SessionAudioCard
            clip={clip}
            studentId={card.studentId}
            classSessionId={card.classSessionId}
            joins={joins}
          />
        </AnimateInFlex>
      ))}
    </AnimatePresence>
  )

  const renderComments = (
    comments: readonly (SessionComment | EarlierComment)[],
    collapsed: boolean,
    widePhotos: boolean,
    animation: HTMLMotionProps<"div">
  ) => {
    const ordered = [...comments].sort((a, b) => {
      const roleA = authorRole(a.moment, joins)
      const roleB = authorRole(b.moment, joins)
      if (roleA === roleB) return 0
      return roleA === "teacher" ? -1 : 1
    })
    return (
      <AnimatePresence initial={false} mode="popLayout">
        {ordered.map((comment, index) => (
          <AnimateInFlex
            key={comment.id}
            animation={animation}
            col
            style={{ minWidth: 0 }}
          >
            {renderComment(comment, index, collapsed, widePhotos)}
          </AnimateInFlex>
        ))}
      </AnimatePresence>
    )
  }

  const renderComment = (
    comment: SessionComment | EarlierComment,
    index: number,
    collapsed: boolean,
    widePhotos: boolean
  ) => {
    const photos = comment.photos
    const earlier = "text" in comment
    return (
      <div
        data-testid="sr-session-comment"
        data-moment-id={comment.id}
        data-earlier={earlier ? "true" : "false"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "stretch",
          minWidth: 0,
          width: "100%",
          paddingTop: index === 0 ? 0 : 10,
          marginTop: index === 0 ? 0 : 10,
          borderTop: index === 0 ? "none" : `1px solid ${theme.border}`,
        }}
      >
        {photos.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              width: "100%",
              minWidth: 0,
            }}
          >
            {photos.map((photo) => (
              <SessionPhotoView
                key={photo.id}
                photo={photo}
                joins={joins}
                thumb={collapsed && !widePhotos}
                wide={widePhotos}
              />
            ))}
          </div>
        ) : null}
        <div style={{ width: "100%", minWidth: 0 }}>
          <StandoutMomentCard
            moment={comment.moment}
            joins={joins}
            variant="item"
            showArtwork={false}
            collapsed={collapsed}
            hidePhotos
            attributionDetail={
              earlier ? classNameFor(comment.moment.classId) : undefined
            }
          />
          <FamilyReactions moment={comment.moment} />
        </div>
      </div>
    )
  }

  const groupLabel = {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "18px",
    marginBottom: 8,
  }

  return (
    <div
      data-testid="sr-student-settled"
      data-student-id={card.studentId}
      data-week={card.week}
      data-session-id={card.classSessionId ?? ""}
      data-cleared={columns.cleared ? "true" : "false"}
      data-collapsed={collapsed ? "true" : "false"}
      data-shared={
        card.items.length > 0 && summary === "shared" ? "true" : "false"
      }
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${columns.cleared ? theme.eric.green : "transparent"}`,
        backgroundColor: columns.cleared
          ? `${theme.eric.green}18`
          : theme.gray[2],
        minWidth: 0,
      }}
    >
      <div
        data-testid="sr-card-toggle"
        onClickCapture={(event) => {
          if (!collapsed) return
          const target = event.target
          if (
            target instanceof Element &&
            target.closest('[data-testid="sr-show-hidden"]')
          ) {
            return
          }
          event.preventDefault()
          event.stopPropagation()
          setOpened(true)
        }}
        onClick={() => {
          if (!columns.cleared || collapsed) return
          setOpened(false)
        }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          minWidth: 0,
          cursor: columns.cleared ? "pointer" : "default",
        }}
      >
        <div
          style={{
            ...headerLine,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: "1 1 auto",
          }}
        >
          {showAvatarImage ? (
            <img
              src={avatarUrl}
              alt=""
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                objectFit: "cover",
                flex: "0 0 auto",
              }}
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span
              aria-hidden
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                flex: "0 0 auto",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.white,
                border: `1px solid ${REVIEW_MUTED}`,
                color: theme.black,
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {avatarLetters}
            </span>
          )}
          {dobLabel ? (
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                flex: "0 0 auto",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {dobLabel}
            </span>
          ) : null}
          {student ? (
            <span
              onClick={(event) => {
                if (collapsed) return
                event.stopPropagation()
              }}
            >
              <ClickableStudentName studentId={student.id} label={nameLabel} />
            </span>
          ) : (
            <span>{card.studentId}</span>
          )}
          {skill ? (
            <span style={{ flex: "0 0 auto", fontSize: 14, fontWeight: 600 }}>
              {`- ${skill}`}
            </span>
          ) : null}
        </div>
        {notifyAt ? (
          <span onClick={(event) => event.stopPropagation()}>
            <SendCountdownLabel notifyAt={notifyAt} />
          </span>
        ) : null}
        {hiddenCount > 0 ? (
          <span onClick={(event) => event.stopPropagation()}>
            <Checkbox
              data-testid="sr-show-hidden"
              checked={showHidden}
              onChange={(event) => {
                const next = event.target.checked
                setShowHidden(next)
                if (next) setOpened(true)
              }}
            >
              Show hidden ({hiddenCount})
            </Checkbox>
          </span>
        ) : null}
      </div>
      {collapsed ? (
        <ClearedCelebration
          size="card"
          burst={justCleared}
          title={`Nothing left to share for ${firstName}`}
        />
      ) : null}
      {collapsed ? null : (
        <>
          {projectName ? (
            <div
              data-testid="sr-session-meta"
              style={{
                ...headerLine,
                fontSize: 13,
                lineHeight: "18px",
                color: REVIEW_MUTED,
              }}
            >
              <span data-testid="sr-session-project">{projectName}</span>
            </div>
          ) : null}
          <RepeatJudgment repeat={repeat} card={card} />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              gap: 12,
              minWidth: 0,
            }}
          >
            <div
              data-testid="sr-session-unshared"
              data-cleared={columns.cleared ? "true" : "false"}
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                flex: "0 1 380px",
                maxWidth: 380,
                minWidth: 0,
              }}
            >
              {renderComments(
                columns.staff.notes,
                false,
                true,
                ANIMATION_BY_KEY.leftSm
              )}
              {renderAudio(open.audio, ANIMATION_BY_KEY.leftSm)}
              {photoDivider(
                open.photos,
                open.notes.length > 0,
                false,
                true,
                ANIMATION_BY_KEY.leftSm
              )}
              {renderComments(open.notes, false, true, ANIMATION_BY_KEY.leftSm)}
              <AnimatePresence initial={false}>
                {columns.cleared ? (
                  <ClearedCelebration
                    key="cleared"
                    size="card"
                    burst={justCleared}
                    title={`Nothing left to share for ${firstName}`}
                  />
                ) : null}
              </AnimatePresence>
              {showHidden &&
              blocked.photos.length +
                blocked.notes.length +
                blocked.audio.length +
                hidden.photos.length +
                hidden.notes.length +
                hidden.audio.length >
                0 ? (
                <div
                  data-testid="sr-left-dont-share"
                  style={{ minWidth: 0, opacity: 0.6 }}
                >
                  <div style={{ ...groupLabel, color: REVIEW_MUTED }}>
                    Hidden
                  </div>
                  {renderAudio(
                    [...blocked.audio, ...hidden.audio],
                    ANIMATION_BY_KEY.leftSm
                  )}
                  {photoDivider(
                    [...blocked.photos, ...hidden.photos],
                    blocked.notes.length + hidden.notes.length > 0,
                    false,
                    true,
                    ANIMATION_BY_KEY.leftSm
                  )}
                  {renderComments(
                    [...blocked.notes, ...hidden.notes],
                    false,
                    true,
                    ANIMATION_BY_KEY.leftSm
                  )}
                </div>
              ) : null}
            </div>
            <aside
              data-testid="sr-comment-history"
              style={{
                position: "relative",
                flex: "1 1 0",
                boxSizing: "border-box",
                borderLeft: `1px solid ${theme.border}`,
                paddingLeft: 12,
                minWidth: 0,
                overflowY: "auto",
              }}
            >
              <div
                data-testid="sr-session-shared"
                style={{ minWidth: 0, width: "100%" }}
              >
                <div style={groupLabel}>Shared</div>
                {shared.photos.length +
                  shared.notes.length +
                  shared.audio.length ===
                0 ? (
                  <span
                    data-testid="sr-comment-history-empty"
                    style={{ ...headerLine, color: REVIEW_MUTED, fontSize: 12 }}
                  >
                    Nothing shared yet
                  </span>
                ) : null}
                {renderAudio(shared.audio, ANIMATION_BY_KEY.right, {
                  marginBottom: 12,
                })}
                {photoDivider(
                  shared.photos,
                  shared.notes.length > 0,
                  false,
                  true,
                  ANIMATION_BY_KEY.right
                )}
                {renderComments(
                  shared.notes,
                  true,
                  true,
                  ANIMATION_BY_KEY.right
                )}
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  )
}

export default SessionReviewCard
